// Weekly hours bar chart with per-day project stacks and capacity indicators.
import { useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { formatHours } from '@/components/ui/formatHours';
import { useT } from '@/i18n/useT';
import { useRovingBarGroup } from '@/features/dashboard/hooks/useRovingBarGroup';
import ChartNavButton from './ChartNavButton';
import ProjectStack from './ProjectStack';
import WeeklyChartLegend from './WeeklyChartLegend';
import {
  DASHBOARD_SECTION_SX,
  DASHBOARD_SECTION_TITLE_SX,
  DASHBOARD_ROVING_LISTBOX_SX,
  rovingBarOptionSx,
} from './dashboardSectionStyles';
import { getChartScaleMaxHours } from './chartScale';

const BarChart = ({
  barData,
  onDayClick,
  canGoBack = false,
  canGoForward = false,
  onPreviousWeek,
  onNextWeek,
}) => {
  const t = useT();
  const [hasFocus, setHasFocus] = useState(false);
  const chartScaleMax = getChartScaleMaxHours(barData);

  const activateDay = useCallback(
    (index) => {
      const day = barData[index];
      if (day?.dateStr) onDayClick(day.dateStr);
    },
    [barData, onDayClick],
  );

  const { activeIndex, setActiveIndex, onKeyDown } = useRovingBarGroup(
    barData.length,
    activateDay,
  );

  const barsLabel = t('dashboard.weeklyChartBars', 'Dagbalken');

  return (
    <Box
      component="section"
      aria-label={t('dashboard.weeklyChart', 'Uren per dag')}
      data-testid="dashboard-week-chart"
      sx={DASHBOARD_SECTION_SX}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={DASHBOARD_SECTION_TITLE_SX}
      >
        {t('dashboard.weeklyChart', 'Uren per dag')}
      </Typography>

      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1.5,
          height: 140,
        }}
      >
        <ChartNavButton
          visible={canGoBack}
          onClick={onPreviousWeek}
          label={t('dashboard.previousWeek', 'Vorige week')}
          side="left"
        >
          <ChevronLeftIcon fontSize="small" />
        </ChartNavButton>

        <Box
          role="listbox"
          tabIndex={0}
          aria-label={barsLabel}
          aria-activedescendant={
            barData[activeIndex]
              ? `dashboard-day-bar-${barData[activeIndex].dateStr || barData[activeIndex].day}`
              : undefined
          }
          data-testid="dashboard-week-chart-bars"
          onFocus={() => setHasFocus(true)}
          onBlur={() => setHasFocus(false)}
          onKeyDown={onKeyDown}
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1.5,
            flex: 1,
            minWidth: 0,
            height: '100%',
            ...DASHBOARD_ROVING_LISTBOX_SX,
          }}
        >
          {barData.map((b, index) => {
            const optionId = `dashboard-day-bar-${b.dateStr || b.day}`;
            const isActive = index === activeIndex;
            return (
              <Box
                key={b.dateStr || b.day}
                id={optionId}
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  setActiveIndex(index);
                  onDayClick(b.dateStr);
                }}
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  height: '100%',
                  cursor: 'pointer',
                  minWidth: 0,
                  borderRadius: 0.5,
                  ...rovingBarOptionSx(isActive, hasFocus),
                  '&:hover .stack-outline': { opacity: 0.8 },
                }}
              >
                <Typography
                  variant="caption"
                  color="text.subtle"
                  sx={{ mb: 0.5, fontSize: '0.7em' }}
                >
                  {formatHours(b.hours)}
                </Typography>
                {b.planned !== null && b.planned !== undefined ? (
                  <Box
                    className="stack-outline"
                    sx={{
                      width: '100%',
                      borderRadius: '3px 3px 0 0',
                      mt: 'auto',
                      border: 1,
                      borderColor: 'divider',
                      borderBottom: 0,
                      display: 'flex',
                      alignItems: 'flex-end',
                      height: `${Math.min((b.planned / chartScaleMax) * 100, 100)}%`,
                    }}
                  >
                    <ProjectStack
                      projects={b.projects}
                      capHours={b.hours}
                      scaleMaxHours={b.planned > 0 ? b.planned : chartScaleMax}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      borderRadius: '3px 3px 0 0',
                      mt: 'auto',
                      overflow: 'hidden',
                      height: `${Math.min((b.hours / chartScaleMax) * 100, 100)}%`,
                    }}
                  >
                    <ProjectStack
                      projects={b.projects}
                      scaleMaxHours={b.hours > 0 ? b.hours : chartScaleMax}
                    />
                  </Box>
                )}
                <Typography
                  variant="caption"
                  color="text.subtle"
                  sx={{ mt: 0.5 }}
                >
                  {b.day}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <ChartNavButton
          visible={canGoForward}
          onClick={onNextWeek}
          label={t('dashboard.nextWeek', 'Volgende week')}
          side="right"
        >
          <ChevronRightIcon fontSize="small" />
        </ChartNavButton>
      </Box>

      <WeeklyChartLegend barData={barData} />
    </Box>
  );
};

export default BarChart;
